import React, { useCallback, useState } from 'react';
import { Combobox } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/24/outline';
import { ComboboxOptions, CreatableComboboxOptions } from './components/ComboboxOptions';
import { ComboboxFormCell } from './components/ComboboxFormCell';
import { SelectOption, SingleComboboxProps } from '../interfaces';
import { Show } from 'components/shared/Show';
import { ComboboxSingleWrapper } from './components/ComboboxSingleWrapper';
import classNames from 'classnames';

const lowNoSpace = (str: string) => str.toLowerCase().replace(/\s+/g, '');

const getFilteredOptions = (options: SelectOption[], query: string): SelectOption[] => {
  if (query === '') return options;
  return options.filter((option: SelectOption) => lowNoSpace(option.label).includes(lowNoSpace(query)));
};

export interface ComboBoxTWProps extends SingleComboboxProps {
  autoComplete?: string;
  isNullable?: boolean;
  isMenuPlacementTop?: boolean;
  formatOptionLabel?: (option: SelectOption) => React.ReactNode;
  onClearSelection?: () => void;
}

export const ComboBoxTW: React.FC<ComboBoxTWProps> = ({ options, isMenuPlacementTop, formatOptionLabel, ...rest }) => {
  const [query, setQuery] = useState<string>('');

  const filteredOptions = getFilteredOptions(options, query);

  return (
    <ComboboxSingleWrapper setQuery={setQuery} options={options} {...rest}>
      <ComboboxOptions
        filteredOptions={filteredOptions}
        isMenuPlacementTop={!!isMenuPlacementTop}
        formatOptionLabel={formatOptionLabel}
      />
    </ComboboxSingleWrapper>
  );
};

export interface IComboBoxMultipleTWProps {
  value: SelectOption[] | undefined;
  onChange: (option: SelectOption[]) => void;
  disabled?: boolean;
  options: SelectOption[];
  className?: string;
  placeholder?: string;
  errorText?: string;
  hintText?: string;
  labelText?: string;
  isRequired?: boolean;
  isMenuPlacementTop?: boolean;
}

export const ComboBoxMultipleTW: React.FC<IComboBoxMultipleTWProps> = ({
  value,
  onChange,
  isMenuPlacementTop,
  className,
  errorText,
  hintText,
  labelText,
  isRequired,
  placeholder = '',
  options,
  disabled,
}) => {
  const [query, setQuery] = useState<string>('');
  const filteredOptions = getFilteredOptions(options, query);
  const getDisplayValue = useCallback((item?: SelectOption | null) => item?.label ?? placeholder, [placeholder]);

  return (
    <Combobox as="div" value={value} onChange={onChange} multiple className={className} disabled={disabled}>
      <ComboboxFormCell errorText={errorText} hintText={hintText} labelText={labelText} isRequired={isRequired}>
        <Combobox.Button
          className={classNames(
            'min-h-10 w-full max-w-[60rem] rounded-md bg-white py-1.5 pl-3 pr-10 shadow-sm ring-1 ring-inset ring-gray-300 focus-within:ring-2 focus-within:ring-inset focus-within:ring-slate-800 disabled:bg-gray-100',
            className
          )}
        >
          <Show when={!value}>
            <Combobox.Input
              autoComplete="off"
              className=" w-full text-gray-900 focus:outline-none sm:text-sm sm:leading-6"
              onChange={(event) => setQuery(event.target.value)}
              displayValue={getDisplayValue}
            />
          </Show>
          <div className={'flex flex-wrap justify-start'}>
            {value &&
              value.map((option: SelectOption) => (
                <div
                  key={`${option.value}`}
                  className="mb-1 mr-1 inline-flex h-5 items-center rounded-full bg-gray-200 px-2 py-1"
                >
                  <span className="mr-1 text-nowrap text-xs">{option.label}</span>
                  <span
                    className="text-xs"
                    onClick={(event) => {
                      event.stopPropagation();
                      const newValues = value.filter((v) => v.value !== option.value);
                      onChange(newValues);
                    }}
                  >
                    &#10005;
                  </span>
                </div>
              ))}
          </div>
          <div className="absolute inset-y-0 right-0 flex items-center rounded-r-md px-2 focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </div>
        </Combobox.Button>
        <ComboboxOptions filteredOptions={filteredOptions} isMenuPlacementTop={!!isMenuPlacementTop} />
      </ComboboxFormCell>
    </Combobox>
  );
};

export const CreatableComboBoxTW: React.FC<ComboBoxTWProps> = ({
  options,
  isMenuPlacementTop,
  formatOptionLabel,
  ...rest
}) => {
  const [query, setQuery] = useState<string>('');

  const filteredOptions = getFilteredOptions(options, query);

  return (
    <ComboboxSingleWrapper setQuery={setQuery} options={options} {...rest}>
      <CreatableComboboxOptions
        filteredOptions={filteredOptions}
        isMenuPlacementTop={!!isMenuPlacementTop}
        query={query}
        formatOptionLabel={formatOptionLabel}
      />
    </ComboboxSingleWrapper>
  );
};
